import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiPatch } from "@/lib/api";
import { TASK_ROLE_LABELS, type TaskAccess } from "./access";

export function TaskAccessPanel({ access }: { access: TaskAccess }) {
  const queries = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const managerIds = new Set(access.managers.map(member => member.id));

  async function setManager(userId: string, enabled: boolean) {
    setBusy(userId); setError(null); setNotice(null);
    try {
      await apiPatch(`/v1/tasks/managers/${encodeURIComponent(userId)}?workspace_id=${encodeURIComponent(access.workspace_id)}`, { enabled });
      await Promise.all([
        queries.invalidateQueries({ queryKey: ["task-access"] }),
        queries.invalidateQueries({ queryKey: ["members", access.workspace_id] }),
      ]);
      setNotice(enabled ? "タスク管理者に指定しました。" : "タスク管理者の指定を解除しました。");
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  }

  return <section aria-label="タスクの権限" className="border-b border-border-subtle bg-canvas px-6 py-3 text-sm">
    <div className="flex flex-wrap items-center gap-3">
      <span className="rounded bg-surface-base px-3 py-2 font-medium text-fg-primary">{TASK_ROLE_LABELS[access.role]}</span>
      <p className="text-fg-secondary">{access.can_manage_tasks ? "ワークスペース全員のタスクを確認・管理できます。" : "自分が担当・作成したタスクを確認できます。"}</p>
      {access.can_manage_managers && <button type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)} className="ml-auto min-h-11 rounded-md border border-border-default px-3 text-fg-secondary hover:bg-surface-raised">タスク管理者を指定</button>}
    </div>
    {error && <p role="alert" className="mt-2 text-danger">{error}</p>}
    {notice && <p role="status" className="mt-2 text-success">{notice}</p>}
    {expanded && access.can_manage_managers && <div className="mt-3 space-y-3">
      <p className="text-xs text-fg-muted">システム管理者がタスク管理者を指定し、タスク管理者が各タスクの担当者を割り当てます。</p>
      <ul className="divide-y divide-border-subtle rounded-md border border-border-default">
        {access.members.map(member => {
          const system = ["owner", "admin"].includes(member.role);
          const manager = managerIds.has(member.id);
          return <li key={member.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
            <span className="min-w-0 flex-1 break-words text-fg-primary">{member.display_name || member.email}</span>
            <span className="text-xs text-fg-muted">{system ? "システム管理者" : manager ? "タスク管理者" : "担当者"}</span>
            {["member", "manager"].includes(member.role) && <button type="button" disabled={busy !== null} onClick={() => void setManager(member.id, !manager)} className="min-h-11 rounded-md border border-border-default px-3 text-xs text-fg-secondary hover:bg-surface-raised disabled:opacity-50">{busy === member.id ? "保存中…" : manager ? "指定を解除" : "管理者に指定"}</button>}
          </li>;
        })}
      </ul>
    </div>}
  </section>;
}
